// P4 AI Tutor -- raw HTTP client.
//
// WP-03 (doc 10/doc 12, this document's own §F/§G) corrects `getSession`/
// `getTurns`/`createTurn` to the same Subway-mediated, same-origin,
// cookie-session model `createSession` already uses. The browser sends no
// `x-course-id`, `x-client-id`, `Authorization`, or AI Tutor base URL
// credential of any kind; Subway derives all upstream context server-side
// from the authenticated session and the `courseName`/`sessionId` path
// segments. WP-03-CHAT-CORRECTION-R3 (doc 11) moved `getSessions`, and
// STUDENT CHAT V2 moved `createFeedback` and `createUploadUrl`, into that
// mediated group -- so the dormant direct-AI-Tutor-host client
// (`aiTutorAxiosInstance`/`request()`/`getAuthHeaders()`) had no caller left
// and is gone.
//
// The one call that does NOT go to Subway is `uploadToPresignedPost`: by the
// upstream's own design the image bytes go from the browser straight to the
// short-lived S3 presigned POST that Subway handed out -- with no Borna
// credential of any kind (the presigned `fields` authorize it).
//
// Transport only: every function here takes/returns Track-B-shaped DTOs
// (dto.js) and never touches domain.js or the store.

import config from '@/config'
import { httpGet, httpPost } from '../NetworkService'

const API_ROOT = `${config.serverRoot}/api`

/**
 * Shared transport for every Subway-mediated AI Tutor call (`createSession`,
 * and WP-03's `getSession`/`getTurns`/`createTurn` below) -- same-origin,
 * cookie-session `NetworkService` calls, never `aiTutorAxiosInstance`/
 * `getAuthHeaders()`. The browser sends no `x-course-id`, `x-client-id`,
 * `Authorization`, or AI Tutor base URL credential of any kind for any of
 * these; Subway derives all upstream context server-side from the
 * authenticated session and the `courseName`/`sessionId` path segments.
 * @param {'get'|'post'} method
 * @param {string} path -- already under `${API_ROOT}/ai-tutor/...`
 * @param {unknown} [data] -- POST body only
 * @returns {Promise<unknown>}
 */
function subwayMediatedRequest(method, path, data, requestConfig) {
  const call =
    method === 'post' ? httpPost(path, data, requestConfig) : httpGet(path)
  return call
    .then(response => response.data)
    .catch(err => {
      // Preserves the existing ApiError shape ({status, body}) that
      // adapter.normalizeTutorError/apiErrorToTutorError already consume.
      if (err.response) {
        throw { status: err.response.status, body: err.response.data }
      }
      throw { status: 0, body: err.message }
    })
}

/**
 * P4 AI Tutor WP-02C-2 (doc 10, doc 11 §A/§F) -- Subway-mediated,
 * same-origin, cookie-session call.
 * @param {string} courseName -- education_courses.name (doc 10 §11)
 * @param {import('./dto').CreateTutorSessionDto} dto -- always {}
 * @returns {Promise<import('./dto').TutorSessionResponseDto>}
 */
export function createSession(courseName, dto) {
  return subwayMediatedRequest(
    'post',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(courseName)}/sessions`,
    dto
  )
}

/**
 * AT-API-020-H1 (doc 34/01 §3.2) -- the course's Tutor catalog under API
 * 0.2.0: the actions the course offers and whether a teacher stands behind
 * it. Same Subway-mediated, same-origin, cookie-session transport as every
 * call here: the browser names only the course; Subway derives the Tutor
 * course and the credential, caches the answer briefly and drops that cache
 * when a turn's action is refused. No body, no credits.
 * @param {string} courseName -- education_courses.name
 * @returns {Promise<import('./dto').TutorCatalogDto>}
 */
export function getCatalog(courseName) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(courseName)}/catalog`
  )
}

/**
 * 36_TUTOR_POST_UAT_CORRECTIONS TC-2 (doc 36/01 §3) -- the chapters a Learn
 * Setup lists, from the course outline Subway reads for THIS Student (no
 * cache, no body, no credits, no Tutor turn). The browser names only the
 * course; every failure simply leaves the Setup's own suggestions in place.
 * @param {string} courseName -- education_courses.name
 * @returns {Promise<import('./dto').CourseOutlineDto>}
 */
export function getCourseOutline(courseName) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(courseName)}/outline`
  )
}

/*
 * CAT-1A (doc 07 §12.2, doc 20 §13, human-authorized 2026-09-26) -- the
 * Reports read model: three pure Borna database reads on the same
 * Subway-mediated, cookie-session transport. None of them reaches Synapse;
 * opening a conversation still goes through `getSession`/`getTurns` below
 * with the row's `courseName` + `synapseSessionId`.
 */

/**
 * @returns {Promise<import('./dto').ReportsSummaryDto>}
 */
export function getReportsSummary() {
  return subwayMediatedRequest('get', `${API_ROOT}/ai-tutor/reports/summary`)
}

/**
 * @param {{course?: string, status?: string, page?: number, limit?: number}} [query]
 *   `status` (UX2-03, doc 30 §5.2): one of awaiting | handoff | understood |
 *   confused | noFeedback | teacherResolved -- the server's report bucket.
 * @returns {Promise<import('./dto').Paginated<import('./dto').ConversationDto>>}
 */
export function getConversations(query = {}) {
  const params = []
  if (query.course) params.push(`course=${encodeURIComponent(query.course)}`)
  if (query.status) params.push(`status=${encodeURIComponent(query.status)}`)
  if (query.page) params.push(`page=${encodeURIComponent(query.page)}`)
  if (query.limit) params.push(`limit=${encodeURIComponent(query.limit)}`)
  const search = params.length ? `?${params.join('&')}` : ''
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/conversations${search}`
  )
}

/**
 * @param {string} conversationId -- the Borna catalog row id
 * @returns {Promise<import('./dto').ConversationDto>}
 */
export function getConversation(conversationId) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/conversations/${encodeURIComponent(conversationId)}`
  )
}

/**
 * POST-UAT-UX / PUX-S (doc 35/01 §2.2) -- the Borna conversation of one of
 * the Student's own sessions (also before its first exchange), so the main
 * chat can open that conversation's Board. A pure Borna read.
 * @param {string} courseName
 * @param {string} sessionId -- the Synapse session id
 * @returns {Promise<{conversationId: string}>}
 */
export function getSessionConversation(courseName, sessionId) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(
      courseName
    )}/sessions/${encodeURIComponent(sessionId)}/conversation`
  )
}

/**
 * WP-03-CHAT-CORRECTION-R3 (doc 11) -- the basic session list, the reference
 * console's `getSessions()` (`GET /tutor/sessions?page=1&limit=30`), reached the
 * only way the browser is allowed to reach the AI Tutor: through Subway's
 * same-origin, cookie-session mediation, with `page`/`limit` fixed server-side
 * exactly like `getTurns` (never client-controlled).
 *
 * BACKEND PREREQUISITE (recorded in doc 11, NOT built by this package):
 * upchieve-subway does not expose this route yet. Until it does, this call
 * rejects with a 404 and the session list fails soft (chat is unaffected).
 * @param {string} courseName -- education_courses.name
 * @returns {Promise<import('./dto').Paginated<import('./dto').TutorSessionResponseDto>>}
 */
export function getSessions(courseName) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(courseName)}/sessions`
  )
}

/**
 * WP-03 (doc 10 §13, doc 12 §8/§9) -- Subway-mediated, same-origin,
 * cookie-session call. Corrected from the dormant direct-AI-Tutor-host shape
 * this function had before WP-03 (never invoked by any component until now).
 * @param {string} courseName -- education_courses.name, the frozen selected
 *   course from `activeSetupContext.subject` (doc 10 §11)
 * @param {string} sessionId
 * @returns {Promise<import('./dto').TutorSessionResponseDto>}
 */
export function getSession(courseName, sessionId) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(
      courseName
    )}/sessions/${encodeURIComponent(sessionId)}`
  )
}

/**
 * WP-03 (doc 10 §13, doc 12 §8/§9) -- Subway-mediated, same-origin,
 * cookie-session call. `page`/`limit` are fixed server-side (Subway always
 * requests 1/100, matching the proven Track-B contract) -- not accepted as
 * parameters here, so no caller can construct client-controlled pagination.
 * @param {string} courseName
 * @param {string} sessionId
 * @returns {Promise<import('./dto').Paginated<import('./dto').TutorTurnResponseDto>>}
 */
export function getTurns(courseName, sessionId) {
  return subwayMediatedRequest(
    'get',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(
      courseName
    )}/sessions/${encodeURIComponent(sessionId)}/turns`
  )
}

/**
 * WP-03 (doc 10 §13, doc 12 §8/§9) -- Subway-mediated, same-origin,
 * cookie-session call. `dto` is passed through to the request body
 * unmodified (text/mode/resume/attachments, doc 05 §7); WP-03 itself only
 * ever sends `{text}`.
 * @param {string} courseName
 * @param {string} sessionId
 * @param {import('./dto').CreateTutorTurnDto} dto
 * @param {{onSent?: Function}} [options] BUG-046 (P41): `onSent` runs once,
 *   when the request body has been transmitted in full (the browser's upload
 *   progress). The request itself -- URL, body, headers -- is unchanged.
 * @returns {Promise<import('./dto').TutorTurnResponseDto>}
 */
export function createTurn(courseName, sessionId, dto, options) {
  const onSent = options && options.onSent
  let sent = false
  const requestConfig =
    typeof onSent === 'function'
      ? {
          onUploadProgress: progress => {
            if (sent || !progress) return
            const { loaded, total } = progress
            if (!total || loaded < total) return
            sent = true
            onSent()
          },
        }
      : undefined
  return subwayMediatedRequest(
    'post',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(
      courseName
    )}/sessions/${encodeURIComponent(sessionId)}/turns`,
    dto,
    requestConfig
  )
}

/**
 * STUDENT CHAT V2 -- CORE INTERACTION LOOP (human-authorized 2026-09-22) --
 * «فهمیدم / نفهمیدم» on a tutor turn, moved off the direct AI Tutor host onto
 * the same Subway-mediated, same-origin, cookie-session transport as
 * `createTurn`. Subway accepts only `{turnId, kind: 'understood'|'confused'}`.
 * AT-API-020-H2 (doc 34/01 §3.6): Subway records the tap as a Borna-owned
 * learning event and answers `{id, turnId, kind, recordedAt, repeated}`; no
 * Tutor turn, no re-teach, no credit effect comes back from this call.
 * @param {string} courseName
 * @param {string} sessionId
 * @param {import('./dto').CreateTutorFeedbackDto} dto
 * @returns {Promise<import('./dto').TutorFeedbackResponseDto>}
 */
export function createFeedback(courseName, sessionId, dto) {
  return subwayMediatedRequest(
    'post',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(
      courseName
    )}/sessions/${encodeURIComponent(sessionId)}/feedback`,
    dto
  )
}

/**
 * STUDENT CHAT V2 -- IMAGE ATTACHMENT FLOW (human-authorized 2026-09-22) --
 * a presigned upload for one question photo, through Subway (same trust
 * boundary as `createTurn`). Subway accepts only `{fileFormat}` in
 * jpg/jpeg/png/webp; the returned `{url, fields, filename}` is an S3
 * presigned POST (15 min, Content-Type pinned to the format, 1 byte..10MB).
 * @param {string} courseName
 * @param {string} sessionId
 * @param {import('./dto').CreateTutorAttachmentUploadUrlDto} dto
 * @returns {Promise<import('./dto').UploadUrl>}
 */
export function createUploadUrl(courseName, sessionId, dto) {
  return subwayMediatedRequest(
    'post',
    `${API_ROOT}/ai-tutor/courses/${encodeURIComponent(
      courseName
    )}/sessions/${encodeURIComponent(sessionId)}/attachments/upload-urls`,
    dto
  )
}

/**
 * 37_VOICE_TO_TEXT (doc 37/01 §3.2) -- a presigned upload for ONE recording,
 * through Subway (the tenant key stays server-side). Course-less: the speech
 * service is not a course's. Subway accepts `{fileFormat}` in webm/ogg/m4a and
 * answers `{url, fields, filename}` -- an S3 presigned POST (15 min, the
 * Content-Type pinned, 1 byte..5MB) for `uploadToPresignedPost` below.
 * @param {{fileFormat: 'webm'|'ogg'|'m4a'}} dto
 * @returns {Promise<import('./dto').UploadUrl>}
 */
export function createVoiceUploadUrl(dto) {
  return subwayMediatedRequest(
    'post',
    `${API_ROOT}/ai-tutor/voice/upload-urls`,
    dto
  )
}

/**
 * 37_VOICE_TO_TEXT -- the text of an uploaded recording. The Student is the
 * session's (Subway derives the provider's clientId); the browser names only
 * the `filename` it was given and its interface language. `''` = nothing was
 * heard.
 * @param {{filename: string, uiLocale: 'fa'|'en'}} dto
 * @returns {Promise<{text: string}>}
 */
export function createVoiceTranscription(dto) {
  return subwayMediatedRequest(
    'post',
    `${API_ROOT}/ai-tutor/voice/transcriptions`,
    dto
  )
}

/**
 * Phase 2 of the attachment upload (Track B §10): a direct browser POST to
 * the presigned S3 URL -- a different host than the Borna API base, with NO
 * Borna auth headers; the presigned-POST `fields` carry authorization.
 * @param {import('./dto').UploadUrl} uploadUrl
 * @param {File|Blob} file
 * @param {{signal?: AbortSignal}} [options] -- 37_VOICE_TO_TEXT: lets the
 *   voice controller abort a stalled upload (the photo path passes none)
 * @returns {Promise<void>}
 */
export async function uploadToPresignedPost(uploadUrl, file, options = {}) {
  const formData = new FormData()
  Object.entries(uploadUrl.fields).forEach(([key, value]) => {
    formData.append(key, value)
  })
  formData.append('file', file, uploadUrl.filename)

  const response = await fetch(uploadUrl.url, {
    method: 'POST',
    body: formData,
    ...(options && options.signal ? { signal: options.signal } : {}),
  })

  if (!response.ok && response.status !== 204) {
    let body = null
    try {
      body = await response.text()
    } catch {
      body = null
    }
    throw { status: response.status, body }
  }
}
